import React from 'react';
import { useNavigate } from 'react-router-dom';
import { useLanguage } from '../../contexts/LanguageContext';
import {
  FaCrown, FaShieldAlt, FaMobileAlt, FaUniversity,
  FaEnvelope, FaChevronRight, FaHome, FaHeadset,
  FaCheckCircle, FaGift, FaArrowRight
} from 'react-icons/fa';

const MahilaSavingsAccount = () => {
  const { language } = useLanguage();
  const navigate = useNavigate();

  // Translations
  const translations = {
    en: {
      // Breadcrumb
      home: 'Home',
      account: 'Account',
      mahilaSavingsAccount: 'Unnati Mahila Savings Account',

      // Hero Section
      womenBanking: 'WOMEN BANKING',
      pageTitle: 'Unnati Mahila Savings Account',
      pageSubtitle: 'Get 1 Lakh Rs. Accidental Insurance Absolutely Free',
      highestInterest: 'Highest 6.5%* Interest Rate',

      // Features
      featuresTitle: 'Account Features',
      feature1: 'Free Check Book',
      feature2: 'Modern Mobile Banking App',
      feature3: 'Free NEFT/RTGS/IMPS Facility',
      feature4: 'UPI QR Code Facility for Businesswomen',
      feature5: 'Accept payments from any bank or app (Google Pay, Phone Pay, etc.) into your account',

      // Benefits
      benefitsTitle: 'Special Benefits',
      benefit1: '1 Lakh Rs. Free Accidental Insurance',
      benefit2: 'Highest Interest Rate of 6.5%*',
      benefit3: 'Zero Balance Account',
      benefit4: 'Free Digital Banking Services',

      // Contact Section
      womenSupport: 'Women Support',
      womenHelpline: 'Customer Care',
      emailSupport: 'Email Support',

      // CTA
      openAccount: 'Open Account Today',
      visitBranch: 'Visit Nearest Branch',
      contactUs: 'Contact Us',


      // Terms
      termsConditions: '*Terms and Conditions Apply'
    },
    mr: {
      // Breadcrumb
      home: 'होम',
      account: 'खाते',
      mahilaSavingsAccount: 'उन्नती महिला बचत खाते',

      // Hero Section - Updated from image
      womenBanking: 'महिला बँकिंग',
      pageTitle: 'उन्नती महिला बचत खाते',
      pageSubtitle: 'मिळवा 1 लाख रु. चा अपघाती विमा अगदी मोफत',


      // Features - Updated from image
      featuresTitle: 'खात्याची वैशिष्ट्ये',
      feature1: 'मोफत चेक बुक.',
      feature2: 'अत्याधुनिक मोबाईल बँकिंग ॲप.',
      feature3: 'मोफत NEFT/RTGS/IMPS सुविधा.',
      feature4: 'व्यवसायिकांसाठी UPI QR कोड ची सुविधा.',
      feature5: 'भारतातील कोणत्याही ॲप (Google Pay, PhonePe) इत्यादींमधून खात्यामध्ये पैसे स्वीकारण्याची सोय.',

      // Benefits - Updated from image
      benefitsTitle: 'विशेष फायदे',
      benefit1: '3 नाती मद्दल बचत खाते',
      benefit2: 'मिळवा 1 लाख रु. चा अपघाती विमा मोफत',
      benefit3: '3 पायरीतील विमा 3 वर्षांसाठी मोफत',


      // Contact Section
      womenSupport: 'महिला समर्थन',
      womenHelpline: 'खाली दिलेल्या नंबरवर लगेच संपर्क करा',
      emailSupport: 'ईमेल',

      // CTA
      openAccount: 'आजच खाते उघडा',
      visitBranch: 'जवळच्या शाखेला भेट द्या',
      contactUs: 'संपर्क साधा',


      // Terms
      termsConditions: '*अटी व शर्ती लागू'
    }
  };

  const t = translations[language];

  const features = [
    { icon: FaCheckCircle, text: t.feature1 },
    { icon: FaUniversity, text: t.feature2 },
    { icon: FaShieldAlt, text: t.feature3 },
    { icon: FaCrown, text: t.feature4 },
    { icon: FaGift, text: t.feature5 }
  ];

  const benefits = [
    { icon: FaGift, text: t.benefit1, color: 'from-pink-500 to-rose-500' },
    { icon: FaCrown, text: t.benefit2, color: 'from-purple-500 to-pink-500' },
    { icon: FaShieldAlt, text: t.benefit3, color: 'from-rose-500 to-pink-500' },
    { icon: FaMobileAlt, text: t.benefit4, color: 'from-pink-500 to-purple-500' }
  ];

  return (
    <div className="font-sans bg-gradient-to-b from-gray-50 to-white">
      {/* Hero Banner */}
      <section
        className="relative h-[600px] overflow-hidden bg-gradient-to-br from-pink-600 via-rose-500 to-purple-600"
      >
        {/* Pattern Overlay */}
        <div className="absolute inset-0 opacity-10">
          <div className="absolute inset-0" style={{
            backgroundImage: `url("data:image/svg+xml,%3Csvg width='100' height='100' viewBox='0 0 100 100' xmlns='http://www.w3.org/2000/svg'%3E%3Cpath d='M11 18c3.866 0 7-3.134 7-7s-3.134-7-7-7-7 3.134-7 7 3.134 7 7 7zm48 25c3.866 0 7-3.134 7-7s-3.134-7-7-7-7 3.134-7 7 3.134 7 7 7zm-43-7c1.657 0 3-1.343 3-3s-.895-3-2-3-3 1.343-3 3 1.343 3 3 3zm63 31c1.657 0 3-1.343 3-3s-.895-3-2-3-3 1.343-3 3 1.343 3 3 3zM34 90c1.657 0 3-1.343 3-3s-.895-3-2-3-3 1.343-3 3 1.343 3 3 3zm56-76c1.657 0 3-1.343 3-3s-.895-3-2-3-3 1.343-3 3 1.343 3 3 3zM12 60c1.105 0 2-.895 2-2s-.895-2-2-2-2 .895-2 2 .895 2 2 2zM35 41c1.105 0 2-.895 2-2s-.895-2-2-2-2 .895-2 2 .895 2 2 2zM12 60c1.105 0 2-.895 2-2s-.895-2-2-2-2 .895-2 2 .895 2 2 2z' fill='%23FFB6C1' fill-opacity='0.4' fill-rule='evenodd'/%3E%3C/svg%3E")`,
            backgroundSize: '300px'
          }}></div>
        </div>

        <div className="relative h-full flex flex-col justify-center">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            {/* Women Banking Badge */}
            <div className="flex items-center mb-6">
              <div className="bg-gradient-to-r from-pink-400 to-rose-400 text-white px-4 py-2 rounded-full font-bold flex items-center">
                <FaCrown className="mr-2" />
                {t.womenBanking}
              </div>
            </div>

            {/* Breadcrumb */}
            <nav className="flex items-center text-pink-100 text-sm mb-8">
              <a href="/" className="flex items-center hover:text-white transition-colors">
                <FaHome className="mr-2" />
                {t.home}
              </a>
              <FaChevronRight className="mx-2 opacity-50" />
              <a href="/account" className="hover:text-white transition-colors">{t.account}</a>
              <FaChevronRight className="mx-2 opacity-50" />
              <span className="font-semibold text-white">{t.mahilaSavingsAccount}</span>
            </nav>

            {/* Main Heading */}
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold text-white mb-4">
              {t.pageTitle}
            </h1>

            {/* Subtitle - Main Offer */}
            <div className="bg-white/10 backdrop-blur-sm border-2 border-white/30 rounded-2xl p-6 mb-4 inline-block">
              <p className="text-2xl md:text-3xl font-bold text-white flex items-center">
                <FaGift className="mr-3 text-yellow-300" />
                {t.pageSubtitle}
              </p>
            </div>


          </div>
        </div>

        {/* Shiny Border Bottom */}
        <div className="absolute bottom-0 left-0 right-0 h-2 bg-gradient-to-r from-transparent via-pink-400 to-transparent"></div>
      </section>

      {/* Main Content */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Left Column - Main Content */}
          <div className="lg:col-span-2 space-y-12">
            {/* Features Section */}
            <section className="bg-gradient-to-br from-pink-50 to-rose-50 rounded-3xl shadow-2xl p-8 border border-pink-200">
              <div className="flex items-center mb-8">
                <div className="w-16 h-16 rounded-2xl bg-gradient-to-r from-pink-500 to-rose-500 flex items-center justify-center mr-6">
                  <FaCrown className="text-white text-2xl" />
                </div>
                <div>
                  <h2 className="text-3xl font-bold text-gray-800">{t.featuresTitle}</h2>
                </div>
              </div>

              <div className="space-y-4">
                {features.map((feature, idx) => (
                  <div key={idx} className="flex items-start p-4 bg-white rounded-xl shadow-sm hover:shadow-md transition-shadow">
                    <feature.icon className="text-pink-500 text-xl mr-4 mt-1 flex-shrink-0" />
                    <p className="text-gray-700 text-lg">{feature.text}</p>
                  </div>
                ))}
              </div>
            </section>


          </div>

          {/* Right Column - Sidebar */}
          <div className="space-y-8">
            {/* Contact Card */}
            <div className="bg-gradient-to-br from-pink-600 to-rose-600 rounded-3xl shadow-xl p-6 border border-pink-500">
              <h3 className="text-xl font-bold text-white mb-6">{t.womenSupport}</h3>
              <div className="space-y-4">
                <div className="flex items-center p-4 bg-pink-500/30 rounded-xl border border-pink-400">
                  <FaHeadset className="text-pink-200 text-2xl mr-4" />
                  <div>
                    <div className="font-medium text-white">{t.womenHelpline}</div>
                    <div className="text-pink-100 font-bold text-lg">95 8283 7032</div>
                  </div>
                </div>

                <div className="flex items-center p-4 bg-pink-500/30 rounded-xl border border-pink-400">
                  <FaEnvelope className="text-pink-200 text-2xl mr-4" />
                  <div>
                    <div className="font-medium text-white">{t.emailSupport}</div>
                    <div className="text-pink-100 text-sm break-all">customercare@shivpratapmultistate.in</div>
                  </div>
                </div>
              </div>
            </div>

            {/* CTA Card */}
            <div className="bg-gradient-to-br from-purple-600 to-pink-600 rounded-3xl shadow-xl p-8 text-center">
              <FaCrown className="text-white text-5xl mx-auto mb-4" />
              <h3 className="text-2xl font-bold text-white mb-4">{t.openAccount}</h3>
              <a
                href={`/branch/${language}`}
                className="inline-block bg-white text-pink-600 font-bold px-8 py-3 rounded-full hover:bg-pink-50 transition-colors"
              >
                {t.visitBranch}
              </a>
            </div>

          </div>
        </div>

        {/* Contact Us Banner */}
        <div className="mt-16 mb-8">
          <div className="relative bg-[#862546] rounded-[2rem] p-8 md:p-12 overflow-hidden shadow-2xl transform hover:scale-[1.01] transition-transform duration-500 mx-auto max-w-5xl">
            {/* Decorative Elements */}
            <div className="absolute -left-16 -bottom-16 w-64 h-64 border-4 border-white/10 rounded-full"></div>
            <div className="absolute right-0 top-0 w-full h-full opacity-10 bg-[url('https://www.transparenttextures.com/patterns/cubes.png')]"></div>

            <div className="relative z-10 flex flex-col items-center justify-center text-center">
              <button
                onClick={() => navigate(`/contact/${language}`)}
                className="bg-white text-[#862546] font-bold text-lg md:text-xl py-4 px-12 rounded-full shadow-lg hover:shadow-white/50 transform hover:-translate-y-1 transition-all duration-300 flex items-center gap-3 group"
              >
                {t.contactUs}
                <FaArrowRight className="text-[#862546] group-hover:translate-x-1 transition-transform" />
              </button>


            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default MahilaSavingsAccount;